import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import Collapse from '@lib/components/Collapse/Collapse';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { materialSymbolsOutlined } from '@lib/enums/common';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Trans from '@lib/components/Trans/Trans';
import { Attachment, AiDocumentationFieldEnum, Tag } from 'graphql-common';
import useEdmDocumentationQueryHook from 'utils/fetch/hooks/useEdmDocumentationQueryHook';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import isRenewalUpdateRequired from 'routes/Documentation/utils/isRenewalUpdateRequired';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { DocumentFieldDetection } from '../utils/useDocumentFieldDetection';
import styles from './DocumentViewDetails.module.scss';

type Props = {
  documentQueryHookResult: ReturnType<typeof useEdmDocumentationQueryHook>;
  isFavorited: boolean;
  isFavoriteLoading: boolean;
  isViewOnly: boolean;
  onCreateVersion: () => void;
  onFavoriteToggle: () => void;
  onSkipRenewal: () => void;
  // AI detection state for description / tags (undefined when AI is off).
  descriptionDetection?: DocumentFieldDetection['description'];
  tagsDetection?: DocumentFieldDetection['tags'];
  credits?: AiCredits | null;
};

export default function DocumentViewDetails({
  documentQueryHookResult,
  isFavorited,
  isFavoriteLoading,
  isViewOnly,
  onCreateVersion,
  onFavoriteToggle,
  onSkipRenewal,
  descriptionDetection,
  tagsDetection,
  credits,
}: Props) {
  const { t } = useTranslation();
  const { data } = documentQueryHookResult;
  const { description, currentVersion, renewalDeadlineDate, tags } = data || {};
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const showRenewalMessage = isRenewalUpdateRequired(renewalDeadlineDate);

  const descriptionUiState =
    descriptionDetection?.uiState ?? AiFieldUiState.None;
  const tagsUiState = tagsDetection?.uiState ?? AiFieldUiState.None;
  const showDescriptionBlock =
    hasTextEditorValue(description) ||
    descriptionUiState === AiFieldUiState.Detecting ||
    descriptionUiState === AiFieldUiState.Failed;
  const showTagsBlock =
    !!(tags && tags.length > 0) ||
    tagsUiState === AiFieldUiState.Detecting ||
    tagsUiState === AiFieldUiState.Failed;

  return (
    <>
      {showDescriptionBlock && (
        <div
          className={classNames(
            entityViewStyles.infoItem,
            entityViewStyles.block,
          )}
        >
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="description" symbolsOutlined />
            <Typography variant="caption">{t('description')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            {descriptionUiState !== AiFieldUiState.None &&
              descriptionDetection && (
                <AiFieldAssist
                  field={AiDocumentationFieldEnum.Description}
                  resolveSource={descriptionDetection.resolveSource}
                  onApplyValue={descriptionDetection.onApplyValue}
                  detectionUiState={descriptionDetection.uiState}
                  detectingLabel={t('ai-generating-description')}
                  onDismiss={descriptionDetection.onDismiss}
                  creditsRemaining={credits}
                  labels={{
                    idleLabel: t('ai-generate-with-ai'),
                    idleCta: t('ai-generate-description-cta'),
                    regenerateCta: t('ai-regenerate'),
                  }}
                />
              )}
            {descriptionUiState === AiFieldUiState.None &&
              hasTextEditorValue(description) && (
                <TextEditor
                  readOnly
                  value={description}
                  className={entityViewStyles.textEditor}
                />
              )}
          </div>
        </div>
      )}
      <div
        className={classNames(
          entityViewStyles.infoItem,
          entityViewStyles.block,
        )}
      >
        <div
          className={classNames(
            entityViewStyles.infoItemLabel,
            entityViewStyles.infoItemTitleRow,
          )}
        >
          <p className={entityViewStyles.infoItemTitle}>
            <MaterialIcon icon="attach_file" symbolsOutlined />
            <Typography variant="caption">{t('attachment')}</Typography>
          </p>
          {!isViewOnly && (
            <Button
              buttonText={t('update')}
              buttonType={ButtonTypes.primaryOutlined}
              leftIcon="upload"
              leftIconClassName={materialSymbolsOutlined}
              onClick={onCreateVersion}
            />
          )}
        </div>
        <div className={entityViewStyles.infoItemContent}>
          {currentVersion?.attachmentAttached ? (
            <DocumentationPreview
              attachment={currentVersion.attachmentAttached}
              notes={currentVersion.notes || undefined}
              view={DocumentationPreviewEnum.DocumentRowCard}
              actions={
                <IconButton
                  icon="visibility"
                  symbolsOutlined
                  type={IconButtonTypes.PrimaryOutlined}
                  onClick={() => setIsFileViewerOpen(true)}
                />
              }
            />
          ) : (
            <Typography variant="body">-</Typography>
          )}
        </div>
      </div>
      {showRenewalMessage && !isViewOnly && (
        <div className={styles.renewalBox}>
          <SupportiveMessage
            color={SupportiveMessageColor.Yellow}
            fullWidth
            text={<Trans i18nKey="document-renewal-due-text" />}
            actions={
              <>
                <Button
                  buttonText={t('skip')}
                  buttonType={ButtonTypes.primaryText}
                  buttonSize={ButtonSizes.small}
                  onClick={onSkipRenewal}
                />
                <Button
                  buttonText={t('update')}
                  buttonType={ButtonTypes.primaryFilled}
                  buttonSize={ButtonSizes.small}
                  onClick={onCreateVersion}
                />
              </>
            }
          />
        </div>
      )}
      {showTagsBlock && (
        <div className={entityViewStyles.infoItemCol}>
          <p className={entityViewStyles.infoItemTitle}>
            <MaterialIcon icon="tag" symbolsOutlined />
            <Typography variant="caption">{t('tags')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            {tagsUiState !== AiFieldUiState.None && tagsDetection && (
              <AiFieldAssist
                field={AiDocumentationFieldEnum.Tags}
                resolveSource={tagsDetection.resolveSource}
                onApplyTagNames={tagsDetection.onApplyTagNames}
                detectionUiState={tagsDetection.uiState}
                detectingLabel={t('ai-generating-tags')}
                onDismiss={tagsDetection.onDismiss}
                creditsRemaining={credits}
                labels={{
                  idleLabel: t('ai-suggest-with-ai'),
                  idleCta: t('ai-suggest-tags-cta'),
                  regenerateCta: t('ai-regenerate'),
                }}
              />
            )}
            {tagsUiState === AiFieldUiState.None &&
              !!(tags && tags.length > 0) && (
                <Collapse minHeight={56}>
                  <div className={entityViewStyles.infoItemContentRow}>
                    {tags.map((tag: Tag) => (
                      <Chip
                        key={tag.id}
                        type={ChipTypes.chipGray}
                        label={tag.name}
                      />
                    ))}
                  </div>
                </Collapse>
              )}
          </div>
        </div>
      )}
      <FileViewerModal
        attachment={currentVersion?.attachmentAttached as Attachment}
        documentationVersionId={currentVersion?.id}
        isOpen={isFileViewerOpen}
        onClose={() => setIsFileViewerOpen(false)}
        useDownloadTool={false}
        useFavoriteTool
        onFavoriteToggle={onFavoriteToggle}
        isFavorited={isFavorited}
        isFavoriteLoading={isFavoriteLoading}
      />
    </>
  );
}
